import {
  Alert,
  Badge,
  Box,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
  alpha,
  useTheme,
  useMediaQuery,
  Button,
  Select,
  MenuItem,
} from '@mui/material';
import {
  AudioDataSourceItemPlayer,
  JsonDataSourceItemViewer,
  CsvDataSourceItemViewer,
  ImageDataSourceItemViewer,
  PdfDataSourceItemViewer,
  VideoDataSourceViewer,
  XmlDataSourceItemViewer,
  ExcelDataSourceItemViewer,
} from '../viewers';
import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirm } from 'material-ui-confirm';
import { useApiContext } from '@/contexts/apiContext';
import ProgressIndicator from '../progressIndicator';
import ErrorMessage from '../errorMessage';
import EventDataViewer from '../viewers/eventDataViewer';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import TimelineIcon from '@mui/icons-material/Timeline';
import { eventNameEquals } from '@/utils';

type Props = { dataSourceItem: any; maxHeight: number; userAppInstance?: any; dataSourceInstance?: any };

/** Derive a human-readable label for a mime type */
const mimeLabel = (mimeType?: string): string => {
  if (!mimeType) return 'File';
  const lower = mimeType.toLowerCase();
  if (lower.startsWith('video')) return 'Video';
  if (lower.startsWith('audio')) return 'Audio';
  if (lower.startsWith('image')) return 'Image';
  if (lower.startsWith('application/json')) return 'JSON';
  if (lower.startsWith('application/xml') || lower.startsWith('text/xml')) return 'XML';
  if (lower.startsWith('application/csv') || lower.startsWith('text/csv')) return 'CSV';
  if (lower.startsWith('application/pdf')) return 'PDF';
  return mimeType.split('/')[1]?.toUpperCase() || 'File';
};

const ViewDataSourceItem = ({ dataSourceItem, maxHeight, userAppInstance, dataSourceInstance }: Props) => {
  const { t } = useTranslation();
  const apiContext = useApiContext();
  const theme = useTheme();
  const confirm = useConfirm();

  const [loading, setLoading] = useState(false);
  const [processingResults, setProcessingResults] = useState<any[]>([]);
  const [error, setError] = useState<any>();
  const [selectedEventId, setSelectedEventId] = useState<string | undefined>();

  const handleDeleteEvent = async (ev: any) => {
    if (!ev?.id) return;
    try {
      await confirm({
        title: 'Delete Event Data',
        description: `Are you sure you want to permanently delete this event record (ID: ${ev.id})? This action cannot be undone.`,
        confirmationText: 'Delete',
        cancellationText: 'Cancel',
        confirmationButtonProps: { color: 'error', variant: 'contained' },
      });

      if (ev.sourceEntityType === 'dataSourceInstance' || ev.sourceEntityType === 'dataSourceDef') {
        const defId = ev.sourceEntityDefId || dataSourceItem?.dataSourceDefId || dataSourceInstance?.dataSourceDefId;
        const instId = ev.sourceEntityId || dataSourceItem?.dataSourceInstanceId || dataSourceInstance?.id;
        await apiContext.deleteDataSourceItemEventData(defId, instId, ev.id);
      } else if (ev.sourceEntityType === 'userAppInstance' || ev.sourceEntityType === 'userAppDef') {
        const defId = ev.sourceEntityDefId || userAppInstance?.userAppDefId;
        const instId = ev.sourceEntityId || userAppInstance?.id;
        await apiContext.deleteUserAppInstanceEventData(defId, instId, ev.id);
      } else {
        const defId = ev.sourceEntityDefId;
        const instId = ev.sourceEntityId;
        await apiContext.deleteAgentInstanceEventData(defId, instId, ev.id);
      }

      setProcessingResults((prev) => prev.filter((e) => e.id !== ev.id));
      if (selectedEventId === ev.id) {
        setSelectedEventId(undefined);
      }
    } catch (err: any) {
      if (err) {
        console.error('Failed to delete event data:', err);
        setError(err?.response?.data?.message || err?.message || 'Failed to delete event data.');
      }
    }
  };

  useEffect(() => {
    const getProcessingResults = async () => {
      if (!dataSourceItem?.id) return;
      setError(null);
      try {
        setLoading(true);
        const results = await apiContext.getDataSourceItemEventsData(
          dataSourceItem.dataSourceDefId,
          dataSourceItem.dataSourceInstanceId,
          dataSourceItem.id,
        );
        setProcessingResults(results);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    getProcessingResults();
  }, [dataSourceItem?.id, dataSourceItem?.dataSourceDefId, dataSourceItem?.dataSourceInstanceId]);

  // Process events to identify superseded errors
  const processedEvents = useMemo(() => {
    const completedSources = new Set<string>();

    const sortedEvents = [...processingResults].sort(
      (a, b) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime(),
    );

    return sortedEvents.map((r) => {
      const sourceKey = `${r.sourceEntityType}-${r.sourceEntityId}`;
      const isSuccess = eventNameEquals(r.eventName, 'ItemProcessingComplete') || eventNameEquals(r.eventName, 'ItemGroupProcessingComplete');
      const isError =
        r.eventPayload?.payloadType === 'ERROR' ||
        r.eventPayload?.payloadType === 'ERROR_STRING' ||
        r.eventName?.toLowerCase().includes('error') ||
        r.eventName?.toLowerCase().includes('fail');

      if (isSuccess) {
        completedSources.add(sourceKey);
      }

      const isSuperseded = isError && completedSources.has(sourceKey);
      return { ...r, isError, isSuperseded };
    });
  }, [processingResults]);

  // Segregate events (App-specific vs Global)
  const { appEvents, otherEvents } = useMemo(() => {
    const agentEvents = processedEvents.filter(
      (r) => r.sourceEntityType === 'agentInstance' || r.sourceEntityType === 'agentDef',
    );

    if (!userAppInstance) {
      return { appEvents: [], otherEvents: agentEvents };
    }

    const appSourceEntities = userAppInstance?.sourceEntities || [];
    const appEventsList = agentEvents.filter((r) =>
      appSourceEntities.find(
        (se: any) =>
          se.sourceEntityId.toString() === r.sourceEntityId.toString() && se.sourceEntityType === r.sourceEntityType,
      ),
    );
    const otherEventsList = agentEvents.filter((r) => !appEventsList.includes(r));
    return { appEvents: appEventsList, otherEvents: otherEventsList };
  }, [processedEvents, userAppInstance]);

  const totalEvents = appEvents.length + otherEvents.length;

  const errorCount = useMemo(() => {
    let count = processedEvents.filter((r) => r.isError && !r.isSuperseded).length;
    if (dataSourceItem?.status === 'FAILED') {
      count += 1;
    }
    return count;
  }, [processedEvents, dataSourceItem?.status]);

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Shared props for all media viewers
  const viewerProps = {
    dataSourceDefId: dataSourceItem?.dataSourceDefId,
    dataSourceInstanceId: dataSourceItem?.dataSourceInstanceId,
    dataSourceItemId: dataSourceItem?.dataSourceItemId || dataSourceItem?.id,
    filename: dataSourceItem?.itemName,
    maxHeight: isMobile ? 550 - 44 - 26 : maxHeight - 44 - 26, // subtract the panel header height, padding and border
    showLabel: false,
    eventId: selectedEventId,
    dataSourceInstance,
    parentItemName: dataSourceItem?.parentItemName,
    pipelineInstanceId: dataSourceItem?.pipelineInstanceId,
  };

  const isParentDocument = useMemo(() => {
    const meta = dataSourceInstance?.dataSourceDef?.metadata || dataSourceItem?.dataSourceDef?.metadata || {};
    if (meta.hasParentChildItems === true && meta.publishParentItemEvents === false) {
      return !dataSourceItem?.parentItemName;
    }
    return false;
  }, [dataSourceItem, dataSourceInstance]);

  const HEADER_H = 44;
  const panelHeight = maxHeight;

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={0}
      sx={{
        height: { xs: 'auto', md: panelHeight },
        overflow: { xs: 'visible', md: 'hidden' },
        bgcolor: 'background.default',
      }}
    >
      {/* ── LEFT: Content Viewer ───────────────────────────── */}
      <Box
        sx={{
          flex: { xs: 'none', md: isParentDocument ? '1 1 100%' : '1 1 55%' },
          height: { xs: '550px', md: '100%' },
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          minWidth: 0,
        }}
      >
        {/* Section header */}
        <Box
          sx={{
            height: HEADER_H,
            minHeight: HEADER_H,
            display: 'flex',
            alignItems: 'center',
            px: 2,
            gap: 1.5,
            borderBottom: 1,
            borderRight: { xs: 0, md: 1 },
            borderColor: 'divider',
            bgcolor: alpha(theme.palette.background.paper, 0.8),
          }}
        >
          <InsertDriveFileIcon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }} />
          <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1, lineHeight: 1 }} noWrap>
            {dataSourceItem?.itemName || 'Content'}
          </Typography>
          {processingResults.length > 0 && (
            <Select
              value={selectedEventId || 'latest'}
              onChange={(e) => {
                const val = e.target.value;
                setSelectedEventId(val === 'latest' ? undefined : (val as string));
              }}
              size="small"
              sx={{
                height: 26,
                fontSize: '0.72rem',
                fontWeight: 600,
                maxWidth: 180,
                bgcolor: 'background.paper',
                '& .MuiSelect-select': { py: 0.5, pr: 3, pl: 1 },
              }}
            >
              <MenuItem value="latest" sx={{ fontSize: '0.72rem' }}>
                Latest Ingested
              </MenuItem>
              {processingResults.map((event) => {
                const { sourceEntityName, sourceEntityType, dateTime } = event;
                const label = sourceEntityName || t(`common.entityType.${sourceEntityType}`);
                const timeStr = dateTime
                  ? new Date(dateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                  : '';
                return (
                  <MenuItem key={event.id} value={event.id} sx={{ fontSize: '0.72rem' }}>
                    {label} ({timeStr})
                  </MenuItem>
                );
              })}
            </Select>
          )}
          {dataSourceItem?.mimeType && (
            <Chip
              label={mimeLabel(dataSourceItem.mimeType)}
              size="small"
              variant="outlined"
              sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }}
            />
          )}
        </Box>

        {/* Viewer body */}
        <Box
          sx={{
            flex: 1,
            overflow: 'hidden',
            p: 1.5,
            borderRight: { xs: 0, md: 1 },
            borderBottom: { xs: 1, md: 0 },
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Paper
            elevation={0}
            sx={{
              flex: 1,
              borderRadius: '10px',
              overflow: 'hidden',
              border: 1,
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {selectedEventId && (
              <Alert
                severity="info"
                variant="filled"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => setSelectedEventId(undefined)}
                    sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.75rem', py: 0 }}
                  >
                    Reset to Latest
                  </Button>
                }
                sx={{
                  borderRadius: 0,
                  py: 0.25,
                  px: 2,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                Viewing historical trace event payload
              </Alert>
            )}
            {dataSourceItem?.mimeType?.startsWith('video') ? (
              <VideoDataSourceViewer {...viewerProps} mimeType={dataSourceItem.mimeType} />
            ) : dataSourceItem?.mimeType?.startsWith('audio') ? (
              <AudioDataSourceItemPlayer {...viewerProps} mimeType={dataSourceItem.mimeType} />
            ) : dataSourceItem?.mimeType?.startsWith('image') ? (
              <ImageDataSourceItemViewer {...viewerProps} mimeType={dataSourceItem.mimeType} />
            ) : dataSourceItem?.mimeType?.toLowerCase()?.startsWith('application/json') ? (
              <JsonDataSourceItemViewer {...viewerProps} />
            ) : dataSourceItem?.mimeType?.toLowerCase()?.startsWith('application/xml') ||
              dataSourceItem?.mimeType?.toLowerCase()?.startsWith('text/xml') ||
              dataSourceItem?.mimeType?.toLowerCase() === 'application/atom+xml' ? (
              <XmlDataSourceItemViewer {...viewerProps} />
            ) : dataSourceItem?.mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
              dataSourceItem?.mimeType === 'application/vnd.ms-excel' ||
              dataSourceItem?.itemName?.toLowerCase()?.endsWith('.xlsx') ||
              dataSourceItem?.itemName?.toLowerCase()?.endsWith('.xls') ? (
              <ExcelDataSourceItemViewer {...viewerProps} />
            ) : dataSourceItem?.mimeType?.toLowerCase()?.startsWith('application/csv') ||
              dataSourceItem?.mimeType?.toLowerCase()?.startsWith('text/csv') ||
              dataSourceItem?.itemName?.toLowerCase()?.endsWith('.csv') ? (
              <CsvDataSourceItemViewer {...viewerProps} separator="," />
            ) : (
              <PdfDataSourceItemViewer {...viewerProps} />
            )}
          </Paper>
        </Box>
      </Box>
      {/* ── RIGHT: Events Traceability ────────────────────── */}
      {!isParentDocument && (
        <Box
          sx={{
            flex: { xs: 'none', md: '1 1 45%' },
            height: { xs: 'auto', md: '100%' },
            display: 'flex',
            flexDirection: 'column',
            overflow: { xs: 'visible', md: 'hidden' },
            minWidth: 0,
          }}
        >
          {/* Section header */}
          <Box
            sx={{
              height: HEADER_H,
              minHeight: HEADER_H,
              display: 'flex',
              alignItems: 'center',
              px: 2,
              gap: 1.5,
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: alpha(theme.palette.background.paper, 0.8),
            }}
          >
            <TimelineIcon
              fontSize="small"
              sx={{ color: errorCount > 0 ? 'error.main' : 'secondary.main', opacity: 0.8 }}
            />
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{ flex: 1, color: errorCount > 0 ? 'error.main' : 'inherit' }}
            >
              Processing Trace
            </Typography>
            {errorCount > 0 && (
              <Chip
                label={`${errorCount} ${errorCount === 1 ? 'Error' : 'Errors'}`}
                size="small"
                color="error"
                sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }}
              />
            )}
            {totalEvents > 0 && (
              <Badge
                badgeContent={totalEvents}
                color={errorCount > 0 ? 'default' : 'primary'}
                sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem' } }}
              >
                <Box sx={{ width: 16, height: 16 }} />
              </Badge>
            )}
          </Box>

          {/* Events body */}
          <Box sx={{ flex: 1, overflowY: { xs: 'visible', md: 'auto' }, p: 1.5 }}>
            {loading ? (
              <ProgressIndicator />
            ) : (
              <Stack spacing={1.5}>
                {error && <ErrorMessage error={error} />}

                {dataSourceItem?.status === 'FAILED' && (
                  <ErrorMessage error={dataSourceItem.error || 'Item processing failed.'} />
                )}

                {/* App Source Events */}
                {appEvents.length > 0 && (
                  <Stack spacing={1.5}>
                    {appEvents.map((r, idx) => (
                      <EventDataViewer
                        key={r.id || idx}
                        eventData={r}
                        isSuperseded={r.isSuperseded}
                        maxHeight={maxHeight - HEADER_H}
                        defaultExpanded={idx === 0}
                        isAppSource={true}
                        onDelete={handleDeleteEvent}
                      />
                    ))}
                  </Stack>
                )}

                {/* Separator */}
                {appEvents.length > 0 && otherEvents.length > 0 && (
                  <Box sx={{ py: 1 }}>
                    <Divider>
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.disabled', textTransform: 'uppercase', letterSpacing: 1 }}
                      >
                        Agents not in App sources
                      </Typography>
                    </Divider>
                  </Box>
                )}

                {/* Other / Global Events */}
                {otherEvents.length > 0 && (
                  <Box>
                    {appEvents.length === 0 && (
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5, px: 0.5 }}>
                        <TravelExploreIcon fontSize="small" color="action" />
                        <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
                          Global Trace Events
                        </Typography>
                        <Chip
                          label={otherEvents.length}
                          size="small"
                          color="default"
                          sx={{ height: 18, fontSize: '0.65rem' }}
                        />
                      </Stack>
                    )}
                    <Stack spacing={1.5}>
                      {otherEvents.map((r, idx) => (
                        <EventDataViewer
                          key={r.id || idx}
                          eventData={r}
                          isSuperseded={r.isSuperseded}
                          maxHeight={maxHeight - HEADER_H}
                          defaultExpanded={appEvents.length === 0 && idx === 0}
                          isAppSource={false}
                          onDelete={handleDeleteEvent}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}

                {appEvents.length === 0 && otherEvents.length === 0 && !loading && (
                  <Box
                    sx={{
                      mt: 4,
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1,
                      opacity: 0.5,
                    }}
                  >
                    <TimelineIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
                    <Typography variant="body2" color="text.secondary">
                      No processing events found for this item.
                    </Typography>
                  </Box>
                )}
              </Stack>
            )}
          </Box>
        </Box>
      )}
    </Stack>
  );
};

export default ViewDataSourceItem;
